/**
 * DBN Monetisation — Opportunity Pipeline + Revenue Forecasting + Issuance Tracker + PoA
 */
import React, { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, ResponsiveContainer } from "recharts";
import { dbnApi, PageHeader, SectionHeader, Badge, LoadingState, fmtEur, fmtN, ProgressBar } from "./dbnUtils";
import { Zap, TrendingUp, DollarSign, Leaf } from "lucide-react";
import DBNPoA from "./DBNPoA";

const STAGE_COLORS = {
  Screening:"#475569", Eligible:"#3b82f6", "PDD Preparation":"#a78bfa",
  Validation:"#f59e0b", Registered:"#0d9488", Verification:"#f97316",
  Issued:"#22c55e", "Not Eligible":"#1e293b"
};

function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:10,
      padding:"10px 14px", fontSize:10 }}>
      <p style={{ fontWeight:700, color:"#f0f9ff", marginBottom:6 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color:p.color }}>
          {p.name}: {fmtEur(p.value)}
        </p>
      ))}
      <p style={{ color:"#475569", marginTop:4, fontSize:9 }}>
        VCU: €{payload[0]?.payload?.vcu_price} · Plastic: €{payload[0]?.payload?.plastic_price}
      </p>
    </div>
  );
}

export default function DBNMonetisation() {
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pipeline");
  const [vcuPrice, setVcuPrice] = useState(15);
  const [plasticPrice, setPlasticPrice] = useState(8);

  useEffect(() => {
    Promise.all([dbnApi.monetisation(), dbnApi.revenueForecast()])
      .then(([r1, r2]) => { setData(r1.data); setForecast(r2.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  const s = data?.summary || {};
  const pipeline = data?.opportunity_pipeline || {};
  const stageOrder = data?.stage_order || [];
  const wf = data?.issuance_workflow || {};
  const fc = forecast?.forecast_years || [];
  const fcSummary = forecast?.summary || {};

  const customRevCarbon  = Math.round((fcSummary.total_carbon_vcu||0) * vcuPrice);
  const customRevPlastic = Math.round((fcSummary.total_plastic_credits||0) * plasticPrice);

  // Adjust forecast data with user prices
  const adjustedForecast = fc.map(y => ({
    ...y,
    carbon_revenue:  Math.round(y.carbon_volume * vcuPrice),
    plastic_revenue: Math.round(y.plastic_volume * plasticPrice),
    total_revenue:   Math.round(y.carbon_volume * vcuPrice + y.plastic_volume * plasticPrice),
  }));

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Environmental Markets & Monetisation"
        subtitle="Opportunity pipeline · Revenue forecasting · 5-year model · Issuance tracking" />

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* Stats bar */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:16 }}>
          {[
            { l:"Carbon Eligible",      v:s.carbon_eligible,                             c:"#34d399" },
            { l:"Plastic Eligible",     v:s.plastic_eligible,                            c:"#60a5fa" },
            { l:"Near Issuance",        v:s.near_issuance,                               c:"#f97316" },
            { l:"Annual Carbon VCUs",   v:fmtN(s.total_carbon_vcu_annual),               c:"#0d9488" },
            { l:"5-Year Revenue Potential", v:fmtEur(fcSummary.five_year_cumulative),    c:"#fbbf24" },
          ].map(x => (
            <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
              borderTop:`3px solid ${x.c}`, borderRadius:12, padding:"12px 14px" }}>
              <p style={{ fontSize:20, fontWeight:900, color:x.c }}>{x.v}</p>
              <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{x.l}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:2, marginBottom:16, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content", overflowX:"auto" }}>
          {[
            {key:"pipeline",    label:"Carbon Credits"},
            {key:"forecast",    label:"Revenue Forecast"},
            {key:"modeller",    label:"Revenue Modeller"},
            {key:"issuance",    label:"Active Issuance"},
            {key:"poa",         label:"Programme of Activities (PoA)"},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background:tab===t.key?"rgba(52,211,153,0.15)":"transparent",
                color:tab===t.key?"#34d399":"#475569", whiteSpace:"nowrap" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Opportunity Pipeline */}
        {tab === "pipeline" && (
          <div>
            <p style={{ fontSize:10, color:"#475569", marginBottom:14 }}>
              Projects progressing through the carbon/plastic credit issuance pipeline.
            </p>
            <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:8 }}>
              {stageOrder.map(stage => {
                const projs = pipeline[stage] || [];
                const color = STAGE_COLORS[stage] || "#475569";
                const totalRev = projs.reduce((a,p) => a + (p.revenue_potential||0), 0);
                return (
                  <div key={stage} style={{ minWidth:140, flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                    <div style={{ background:`${color}20`, border:`1px solid ${color}40`, borderRadius:8,
                      padding:"6px 10px", textAlign:"center" }}>
                      <p style={{ fontSize:9, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.08em" }}>{stage}</p>
                      <p style={{ fontSize:16, fontWeight:900, color:"#f0f9ff" }}>{projs.length}</p>
                      {totalRev > 0 && <p style={{ fontSize:8, color:"#475569" }}>€{(totalRev/1000).toFixed(0)}K/yr</p>}
                    </div>
                    {projs.map(p => (
                      <div key={p.id} style={{ background:"#060f1e", border:`1px solid ${color}30`,
                        borderRadius:8, padding:"6px 8px" }}>
                        <p style={{ fontSize:8.5, fontWeight:600, color:"#94a3b8", lineHeight:1.3, marginBottom:3 }}>{p.name.slice(0,32)}</p>
                        <p style={{ fontSize:7.5, color:"#475569" }}>{p.sector}</p>
                        {(p.carbon_annual||0) > 0 && <p style={{ fontSize:7.5, color:"#34d399" }}>{fmtN(p.carbon_annual)} VCU/yr</p>}
                        {(p.plastic_annual||0) > 0 && <p style={{ fontSize:7.5, color:"#60a5fa" }}>{fmtN(p.plastic_annual)} plastic/yr</p>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 5-Year Revenue Forecast */}
        {tab === "forecast" && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16 }}>
              {[
                {l:"Annual Carbon Revenue (Base)",   v:fmtEur(fcSummary.annual_carbon_potential),  c:"#34d399"},
                {l:"Annual Plastic Revenue (Base)",  v:fmtEur(fcSummary.annual_plastic_potential), c:"#60a5fa"},
                {l:"5-Year Cumulative Potential",    v:fmtEur(fcSummary.five_year_cumulative),     c:"#fbbf24"},
              ].map(x => (
                <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
                  borderLeft:`3px solid ${x.c}`, borderRadius:10, padding:"12px 14px" }}>
                  <p style={{ fontSize:16, fontWeight:900, color:x.c }}>{x.v}</p>
                  <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.07em" }}>{x.l}</p>
                </div>
              ))}
            </div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="5-Year Revenue Forecast (2025–2029)"
                sub="Price ramp-up: VCU €12→€20 · Plastic €7→€12 · Volume ramp: 40%→95% of eligible pipeline" />
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={fc} margin={{ top:10, right:20, bottom:4, left:10 }}>
                  <defs>
                    <linearGradient id="carbonGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="plasticGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#60a5fa" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#0f2033" strokeDasharray="3 3" />
                  <XAxis dataKey="year" tick={{ fill:"#475569", fontSize:10 }} />
                  <YAxis tickFormatter={v => `€${(v/1000).toFixed(0)}K`} tick={{ fill:"#475569", fontSize:9 }} />
                  <Tooltip content={<RevenueTooltip />} />
                  <Legend wrapperStyle={{ fontSize:10, color:"#475569" }} />
                  <Area type="monotone" dataKey="carbon_revenue" name="Carbon Revenue"
                    stroke="#34d399" fill="url(#carbonGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="plastic_revenue" name="Plastic Revenue"
                    stroke="#60a5fa" fill="url(#plasticGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
                <SectionHeader title="Revenue by Sector" sub="Annual credit potential" />
                {(forecast?.by_sector||[]).map(s => (
                  <div key={s.sector} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{s.sector.split(" ")[0]}</span>
                    <div style={{ width:80, height:5, background:"#0f2033", borderRadius:3 }}>
                      <div style={{ height:5, width:`${(s.revenue_potential / (forecast?.by_sector?.[0]?.revenue_potential||1)) * 100}%`,
                        background:"#34d399", borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:9, color:"#64748b", width:50, textAlign:"right" }}>{fmtEur(s.revenue_potential)}</span>
                  </div>
                ))}
              </div>
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
                <SectionHeader title="Revenue by State" sub="Annual credit potential" />
                {(forecast?.by_state||[]).map(s => (
                  <div key={s.state} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{s.state}</span>
                    <div style={{ width:80, height:5, background:"#0f2033", borderRadius:3 }}>
                      <div style={{ height:5, width:`${(s.revenue / (forecast?.by_state?.[0]?.revenue||1)) * 100}%`,
                        background:"#60a5fa", borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:9, color:"#64748b", width:50, textAlign:"right" }}>{fmtEur(s.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Revenue Modeller */}
        {tab === "modeller" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Revenue Sensitivity Model" sub="Adjust market prices to model revenue scenarios" />
              <div style={{ marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:11, color:"#94a3b8" }}>VCU Price: <strong style={{ color:"#34d399" }}>€{vcuPrice}/tCO₂e</strong></span>
                </div>
                <input type="range" min={5} max={50} step={1} value={vcuPrice} onChange={e=>setVcuPrice(+e.target.value)}
                  style={{ width:"100%", accentColor:"#34d399" }} />
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#334155" }}>
                  <span>€5 (low)</span><span>€15 (base)</span><span>€25 (high)</span><span>€50 (premium)</span>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:11, color:"#94a3b8" }}>Plastic Price: <strong style={{ color:"#60a5fa" }}>€{plasticPrice}/credit</strong></span>
                </div>
                <input type="range" min={3} max={20} step={1} value={plasticPrice} onChange={e=>setPlasticPrice(+e.target.value)}
                  style={{ width:"100%", accentColor:"#60a5fa" }} />
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#334155" }}>
                  <span>€3</span><span>€8 (base)</span><span>€15</span><span>€20</span>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  {l:"Carbon Revenue (Annual)",  v:fmtEur(customRevCarbon),              c:"#34d399"},
                  {l:"Plastic Revenue (Annual)", v:fmtEur(customRevPlastic),             c:"#60a5fa"},
                  {l:"Combined Annual",          v:fmtEur(customRevCarbon+customRevPlastic), c:"#fbbf24"},
                  {l:"10-Year Cumulative",       v:fmtEur((customRevCarbon+customRevPlastic)*10), c:"#f97316"},
                ].map(x => (
                  <div key={x.l} style={{ background:"#060f1e", borderRadius:8, padding:"10px 12px", borderLeft:`3px solid ${x.c}` }}>
                    <p style={{ fontSize:14, fontWeight:900, color:x.c }}>{x.v}</p>
                    <p style={{ fontSize:8, color:"#475569", textTransform:"uppercase", letterSpacing:"0.06em", lineHeight:1.4 }}>{x.l}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Top Carbon Opportunities" sub="By annual credit potential" />
              {(data?.carbon_projects||[]).slice(0,8).map(p => (
                <div key={p.id} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8,
                  background:"#060f1e", borderRadius:8, padding:"7px 10px" }}>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>{p.name.slice(0,35)}</p>
                    <p style={{ fontSize:8, color:"#475569" }}>{p.sector} · {p.state}</p>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <p style={{ fontSize:11, fontWeight:700, color:"#34d399" }}>{fmtN(p.annual_credits)} VCU</p>
                    <p style={{ fontSize:8, color:"#475569" }}>€{(p.annual_credits * vcuPrice / 1000).toFixed(0)}K</p>
                  </div>
                  <Badge label={p.status} color={STAGE_COLORS[p.status]||"#475569"} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Issuance */}
        {tab === "issuance" && wf.active_project && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Issuance Progress" sub={wf.active_project.name} />
              <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:14 }}>
                {(wf.stages||[]).map((stage, i) => {
                  const isDone = i <= (wf.stages||[]).indexOf(wf.active_project.current_stage);
                  return (
                    <div key={stage} style={{
                      padding:"2px 7px", borderRadius:5, fontSize:8, fontWeight:600,
                      background:stage===wf.active_project.current_stage?"#22c55e":isDone?"#0d9488":"#0f2033",
                      color:isDone?"#f0f9ff":"#334155"
                    }}>{stage}</div>
                  );
                })}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  {l:"Issued",       v:fmtN(wf.active_project.issued_qty)+" VCUs",   c:"#22c55e"},
                  {l:"Sold",         v:fmtN(wf.active_project.sold_qty)+" VCUs",     c:"#34d399"},
                  {l:"Unsold",       v:fmtN(wf.active_project.unsold_qty)+" VCUs",   c:"#f59e0b"},
                  {l:"Net Revenue",  v:fmtEur(wf.active_project.net_revenue),        c:"#fbbf24"},
                ].map(x => (
                  <div key={x.l} style={{ background:"#060f1e", borderRadius:8, padding:"8px 10px", borderLeft:`3px solid ${x.c}` }}>
                    <p style={{ fontSize:13, fontWeight:900, color:x.c }}>{x.v}</p>
                    <p style={{ fontSize:8, color:"#475569", textTransform:"uppercase" }}>{x.l}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Registry Details" sub={wf.active_project.id} />
              {[
                ["Registry", wf.active_project.registry],
                ["VCS ID", wf.active_project.vcs_id],
                ["Validated by", wf.active_project.validated_by],
                ["Annual credits", `${fmtN(wf.active_project.annual_credits)} VCU`],
                ["Lifetime credits", `${fmtN(wf.active_project.lifetime_credits)} VCU`],
                ["Price per VCU", `€${wf.active_project.price_per_vcu}`],
                ["Gross revenue", fmtEur(wf.active_project.gross_revenue)],
                ["Verification costs", fmtEur(wf.active_project.costs)],
              ].map(([l, v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #0f1e33" }}>
                  <span style={{ fontSize:10, color:"#475569" }}>{l}</span>
                  <span style={{ fontSize:10, color:"#f0f9ff", fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PoA Tab */}
        {tab === "poa" && <DBNPoA />}
      </div>
    </div>
  );
}
